// Motion for the loader: the launcher settling in, the panel lifting open and
// dropping closed. Web Animations API, so the loader ships no animation
// library. Fast (<400ms) and eased rather than bouncy; skipped entirely when
// the visitor prefers reduced motion. Message entrances live in the panel's
// CSS.

function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
}

function run(
  el: HTMLElement,
  keyframes: Keyframe[],
  options: KeyframeAnimationOptions,
  onDone?: () => void,
): void {
  if (reducedMotion() || typeof el.animate !== 'function') {
    onDone?.()
    return
  }
  const animation = el.animate(keyframes, options)
  if (onDone) {
    // Finish exactly once: on the animation's end, or after its duration if
    // the browser never reports one (throttled background tabs, test DOMs).
    let done = false
    const finish = (): void => {
      if (done) return
      done = true
      onDone()
    }
    animation.onfinish = finish
    animation.oncancel = finish
    setTimeout(finish, Number(options.duration ?? 0) + 100)
  }
}

const OUT_QUINT = 'cubic-bezier(0.22, 1, 0.36, 1)'

/** Panel reveal on open, from the launcher's corner. */
export function animatePanelOpen(panel: HTMLElement, origin: string): void {
  run(
    panel,
    [
      { opacity: 0, transform: 'translateY(12px) scale(0.96)', transformOrigin: origin },
      { opacity: 1, transform: 'none', transformOrigin: origin },
    ],
    { duration: 320, easing: OUT_QUINT },
  )
}

/** Reverse of `animatePanelOpen`; `onComplete` collapses the root after it. */
export function animatePanelClose(panel: HTMLElement, origin: string, onComplete: () => void): void {
  run(
    panel,
    [
      { opacity: 1, transform: 'none', transformOrigin: origin },
      { opacity: 0, transform: 'translateY(10px) scale(0.97)', transformOrigin: origin },
    ],
    { duration: 200, easing: 'ease-in' },
    onComplete,
  )
}

/** The launcher easing in on mount and when the panel closes. */
export function animateLauncherIn(launcher: HTMLElement): void {
  run(
    launcher,
    [
      { opacity: 0, transform: 'translateY(10px) scale(0.88)' },
      { opacity: 1, transform: 'none' },
    ],
    { duration: 420, easing: 'cubic-bezier(0.34, 1.4, 0.64, 1)' },
  )
}
